import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { createInkMaterial } from '../render/InkMaterial';

/** Every model here is from KayKit (Kay Lousberg), CC0. See public/models/<pack>/LICENSE.txt. */
const BASE = `${import.meta.env.BASE_URL}models/`;

export const MODELS = {
  // Medieval Hexagon pack: buildings
  blacksmith: 'hex/red/building_blacksmith_red.gltf',
  marketRed: 'hex/red/building_market_red.gltf',
  marketYellow: 'hex/yellow/building_market_yellow.gltf',
  marketBlue: 'hex/blue/building_market_blue.gltf',
  marketGreen: 'hex/green/building_market_green.gltf',
  tavern: 'hex/yellow/building_tavern_yellow.gltf',
  homeA_red: 'hex/red/building_home_A_red.gltf',
  homeB_yellow: 'hex/yellow/building_home_B_yellow.gltf',
  homeA_blue: 'hex/blue/building_home_A_blue.gltf',
  homeB_green: 'hex/green/building_home_B_green.gltf',
  towerA: 'hex/red/building_tower_A_red.gltf',
  towerB: 'hex/blue/building_tower_B_blue.gltf',
  well: 'hex/blue/building_well_blue.gltf',
  castle: 'hex/red/building_castle_red.gltf',
  barracks: 'hex/red/building_barracks_red.gltf',
  range: 'hex/green/building_archeryrange_green.gltf',
  windmill: 'hex/yellow/building_windmill_yellow.gltf',
  stage: 'hex/neutral/building_stage_A.gltf',
  fence: 'hex/neutral/fence_wood_straight.gltf',
  wall: 'hex/neutral/wall_straight.gltf',
  wallGate: 'hex/neutral/wall_straight_gate.gltf',
  // Dungeon Remastered: props
  bannerRed: 'dungeon/gltf/banner_patternA_red.gltf.glb',
  bannerYellow: 'dungeon/gltf/banner_patternB_yellow.gltf.glb',
  bannerShield: 'dungeon/gltf/banner_shield_blue.gltf.glb',
  bannerGreen: 'dungeon/gltf/banner_triple_green.gltf.glb',
  bannerThin: 'dungeon/gltf/banner_thin_red.gltf.glb',
  barrel: 'dungeon/gltf/barrel_large.gltf.glb',
  barrels: 'dungeon/gltf/barrel_small_stack.gltf.glb',
  box: 'dungeon/gltf/box_large.gltf.glb',
  boxes: 'dungeon/gltf/box_stacked.gltf.glb',
  crates: 'dungeon/gltf/crates_stacked.gltf.glb',
  chest: 'dungeon/gltf/chest.glb',
  chestGold: 'dungeon/gltf/chest_gold.glb',
  coinsLarge: 'dungeon/gltf/coin_stack_large.gltf.glb',
  coinsSmall: 'dungeon/gltf/coin_stack_small.gltf.glb',
  bottleGreen: 'dungeon/gltf/bottle_A_green.gltf.glb',
  bottleBrown: 'dungeon/gltf/bottle_B_brown.gltf.glb',
  keg: 'dungeon/gltf/keg.gltf.glb',
  tableLong: 'dungeon/gltf/table_long_tablecloth_decorated_A.gltf.glb',
  tableMed: 'dungeon/gltf/table_medium_tablecloth.gltf.glb',
  tableSmall: 'dungeon/gltf/table_small_decorated_A.gltf.glb',
  shelf: 'dungeon/gltf/shelf_small.gltf.glb',
  stool: 'dungeon/gltf/stool.gltf.glb',
  swordShield: 'dungeon/gltf/sword_shield.gltf.glb',
  swordShieldGold: 'dungeon/gltf/sword_shield_gold.gltf.glb',
  torch: 'dungeon/gltf/torch_lit.gltf.glb',
  plateFood: 'dungeon/gltf/plate_food_A.gltf.glb',
  // Restaurant Bits: produce
  crateCarrots: 'food/gltf/crate_carrots.gltf',
  crateTomatoes: 'food/gltf/crate_tomatoes.gltf',
  crateBuns: 'food/gltf/crate_buns.gltf',
  crateCheese: 'food/gltf/crate_cheese.gltf',
  cratePotatoes: 'food/gltf/crate_potatoes.gltf',
  // Adventurers: gear you can buy and hold
  sword: 'gear/gltf/sword_1handed.gltf',
  greatsword: 'gear/gltf/sword_2handed.gltf',
  axe: 'gear/gltf/axe_1handed.gltf',
  waraxe: 'gear/gltf/axe_2handed.gltf',
  shieldRound: 'gear/gltf/shield_round.gltf',
  shieldSquare: 'gear/gltf/shield_square.gltf',
  shieldSpikes: 'gear/gltf/shield_spikes.gltf',
  shieldBadge: 'gear/gltf/shield_badge_color.gltf',
  crossbow: 'gear/gltf/crossbow_1handed.gltf',
  dagger: 'gear/gltf/dagger.gltf',
  staff: 'gear/gltf/staff.gltf',
  spellbook: 'gear/gltf/spellbook_open.gltf',
  mug: 'gear/gltf/mug_full.gltf',
  quiver: 'gear/gltf/quiver.gltf',
  arrows: 'gear/gltf/arrow_bundle.gltf',
  smokebomb: 'gear/gltf/smokebomb.gltf',
  wand: 'gear/gltf/wand.gltf',
  // More of the realm (Medieval Hexagon)
  church: 'hex/red/building_church_red.gltf',
  lumbermill: 'hex/yellow/building_lumbermill_yellow.gltf',
  mine: 'hex/red/building_mine_red.gltf',
  watermill: 'hex/blue/building_watermill_blue.gltf',
  homeA_yellow: 'hex/yellow/building_home_A_yellow.gltf',
  homeB_red: 'hex/red/building_home_B_red.gltf',
  homeA_green: 'hex/green/building_home_A_green.gltf',
  homeB_blue: 'hex/blue/building_home_B_blue.gltf',
  towerRed: 'hex/red/building_tower_B_red.gltf',
  keep: 'hex/yellow/building_castle_yellow.gltf',
  // Dungeon pieces for the dragon's lair
  dWall: 'dungeon/gltf/wall.gltf.glb',
  dWallArched: 'dungeon/gltf/wall_arched.gltf.glb',
  dDoorway: 'dungeon/gltf/wall_doorway.glb',
  dFloor: 'dungeon/gltf/floor_tile_large.gltf.glb',
  dTorch: 'dungeon/gltf/torch_mounted.gltf.glb',
  dPillar: 'dungeon/gltf/pillar_decorated.gltf.glb',
  dColumn: 'dungeon/gltf/column.gltf.glb',
  dStairs: 'dungeon/gltf/stairs.gltf.glb',
} as const;

export type ModelKey = keyof typeof MODELS;

const loader = new GLTFLoader();
const sources = new Map<ModelKey, THREE.Object3D>();

/** Load every model once, one at a time (parallel GLB parses can drop embedded textures). */
export async function loadModels(keys: ModelKey[] = Object.keys(MODELS) as ModelKey[], onProgress?: (f: number) => void): Promise<void> {
  let i = 0;
  for (const k of keys) {
    if (!sources.has(k)) {
      const gltf: GLTF = await loader.loadAsync(BASE + MODELS[k]);
      sources.set(k, inkify(gltf.scene));
    }
    onProgress?.(++i / keys.length);
  }
}

/** Swap every material for the ink material, keeping the colour texture. One outline id per part. */
function inkify(root: THREE.Object3D): THREE.Object3D {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const src = mesh.material as THREE.MeshStandardMaterial;
    mesh.material = createInkMaterial({
      map: src.map ?? null,
      color: src.map ? 0xffffff : src.color?.getHex() ?? 0xffffff,
      band: [0.16, 0.28],
      lineBreak: 0.18,
      lineScale: 6,
      tone: 0.03,
    });
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
  return root;
}

export type PlaceOpts = {
  /** Target height in world units (uniform scale). */
  height?: number;
  /** Or a raw uniform scale. */
  scale?: number;
  /** Sit the bottom on y=0 and centre it on x/z. Default true. */
  ground?: boolean;
};

/**
 * A fresh copy of a model, normalised so its base is at y=0. Geometry and
 * materials are shared with the source (cheap); clone materials if you need to tint.
 */
export function model(key: ModelKey, opts: PlaceOpts = {}): THREE.Group {
  const src = sources.get(key);
  if (!src) throw new Error(`Model not loaded: ${key}`);
  const inner = src.clone(true);
  const box = new THREE.Box3().setFromObject(inner);
  const size = box.getSize(new THREE.Vector3());
  const s = opts.scale ?? (opts.height ? opts.height / size.y : 1);
  inner.scale.multiplyScalar(s);
  if (opts.ground ?? true) {
    const c = box.getCenter(new THREE.Vector3());
    inner.position.set(-c.x * s, -box.min.y * s, -c.z * s);
  }
  const g = new THREE.Group();
  g.add(inner);
  g.userData.size = size.multiplyScalar(s);
  return g;
}

/** Footprint radius of a placed model (for crowd avoidance and colliders). */
export function footprint(obj: THREE.Object3D): number {
  const s = obj.userData.size as THREE.Vector3 | undefined;
  return s ? Math.max(s.x, s.z) * 0.5 : 1;
}
